<template>
    <div class="relative w-full">
        <label
            v-if="floatingLabel"
            :class="[
                'absolute z-10 text-[11px] leading-none transition-all duration-200 px-1 rounded',
                iconBack ? 'left-[42px]' : 'left-3',
                'top-1',
                hasValue ? 'text-blue-600' : 'text-gray-500',
            ]"
            aria-hidden="true"
        >
            {{ floatingLabel }}
        </label>

        <div
            @click="toggleModal"
            :class="[
                'h-10 min-h-[40px] flex items-center gap-2 rounded-lg border bg-white text-sm',
                'px-3 cursor-pointer select-none transition ring-0 outline-none',
                isModalOpen ? 'border-blue-500' : 'border-gray-300',
                disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white',
                cssClass,
            ]"
            :aria-disabled="disabled"
        >
            <span v-if="iconBack" class="shrink-0">
                <AppIcon
                    :icon="iconBack"
                    class="text-gray-600"
                    width="18"
                    height="18"
                />
            </span>

            <span :class="displayedLabelClass" class="truncate flex-1">
                {{ displayedDate }}
            </span>
        </div>

        <teleport to="body">
            <transition name="fade">
                <div
                    v-if="isModalOpen && !disabled"
                    :class="
                        dialog
                            ? 'fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4'
                            : 'fixed z-[9999]'
                    "
                    :style="dialog ? null : popoverStyle"
                    @click.self="dialog && closeModal()"
                    @click.stop
                    ref="popover"
                >
                    <div
                        class="bg-white border border-gray-100 shadow-2xl rounded-xl"
                        :class="
                            dialog
                                ? 'relative max-h-[85vh] overflow-auto'
                                : 'overflow-hidden'
                        "
                        :style="
                            dialog
                                ? { width: 'min(360px, 92vw)' }
                                : { width: `${panelWidth}px` }
                        "
                    >
                        <BaseDate
                            :class="
                                dialog
                                    ? '!static !mt-0 w-full !max-w-none !shadow-none'
                                    : ''
                            "
                            ref="datePicker"
                            :selectedDate="normalizedModel"
                            :checkInDate="checkInDate"
                            :position="position"
                            :applyAvailabilityWindow="applyAvailabilityWindow"
                            :availabilityWindowDays="availabilityWindowDays"
                            @dateSelected="handleDateSelected"
                            @focus-month="focusOnCheckInMonth"
                            @close="closeModal"
                        />
                    </div>
                </div>
            </transition>
        </teleport>
    </div>
</template>

<script>
import BaseDate from "./BaseDate.vue";
import { format } from "date-fns";

export default {
    name: "BaseDatePickerCompact",
    components: { BaseDate },
    props: {
        floatingLabel: { type: String, default: "" },
        placeholder: { type: String, default: "Select date" },
        iconBack: { type: String, default: "mdi:calendar" },
        modelValue: { type: [Date, String, Number, null], default: null },
        position: {
            type: String,
            default: "right",
            validator: (v) => ["left", "right"].includes(v),
        },
        checkInDate: { type: [Date, String, Number, null], default: null },
        disabled: { type: Boolean, default: false },
        cssClass: { type: String, default: "" },
        roomId: { type: String, default: null },
        roomTypeId: { type: String, default: null },
        matchTriggerWidth: { type: Boolean, default: true },
        maxPanelWidth: { type: Number, default: 420 },
        minPanelWidth: { type: Number, default: 300 },
        isOpen: { type: Boolean, default: false },
        // ponytail: dialog utk trigger di panel sempit/scrollable (sidebar filter mobile)
        dialog: { type: Boolean, default: false },
        applyAvailabilityWindow: { type: Boolean, default: false },
        availabilityWindowDays: { type: Number, default: null },
    },
    data() {
        return {
            internalIsOpen: false,
            roomIdState: this.roomId,
            roomTypeIdState: this.roomTypeId,
            popoverStyle: { top: "0px", left: "0px" },
            panelWidth: 340,
            localDate: null,
        };
    },
    computed: {
        isModalOpen() {
            // Jika isOpen prop diberikan, gunakan itu (controlled)
            // Jika tidak, gunakan internal state (uncontrolled)
            return this.isOpen !== undefined ? this.isOpen : this.internalIsOpen;
        },
        normalizedModel() {
            return this.normalizeDate(this.modelValue);
        },
        hasValue() {
            return !!this.normalizedModel;
        },
        displayedDate() {
            const d = this.normalizedModel;
            return d ? format(d, "dd MMM yyyy") : this.placeholder;
        },
        displayedLabelClass() {
            return this.hasValue ? "text-gray-800" : "text-gray-500";
        },
    },
    watch: {
        roomId(v) {
            this.roomIdState = v;
        },
        roomTypeId(v) {
            this.roomIdState = v;
        },
        modelValue(v) {
            const d = this.normalizeDate(v);
            if (d) this.localDate = new Date(d);
        },
        isOpen(newVal) {
            // Saat isOpen berubah dari luar (controlled mode)
            if (newVal && !this.internalIsOpen) {
                this.openModal();
            }
        },
    },
    methods: {
        setCurrentMonth(date) {
            if (this.$refs.datePicker && date) {
                this.$refs.datePicker.setCurrentMonth(date);
            }
        },
        toggleModal() {
            if (this.disabled) return;
            
            // Jika controlled (isOpen prop ada), emit event
            if (this.isOpen !== undefined) {
                if (!this.isOpen) {
                    this.$emit("open");
                } else {
                    this.$emit("close");
                }
            } else {
                // Jika uncontrolled, gunakan internal state
                this.internalIsOpen = !this.internalIsOpen;
                if (this.internalIsOpen) {
                    this.openModal();
                }
            }
        },
        openModal() {
            // Seed local date
            this.localDate = this.normalizedModel
                ? new Date(this.normalizedModel)
                : this.today();
            // Set current month ke checkInDate jika ada (untuk checkOut picker)
            if (this.checkInDate && this.$refs.datePicker) {
                this.$nextTick(() => {
                    this.setCurrentMonth(this.checkInDate);
                });
            }
            this.$nextTick(this.reposition);
        },
        closeModal() {
            // Jika controlled, emit event
            if (this.isOpen !== undefined) {
                this.$emit("close");
            } else {
                // Jika uncontrolled, update internal state
                this.internalIsOpen = false;
            }
        },
        handleDateSelected(selectedDate) {
            const d = this.normalizeDate(selectedDate);
            if (d) {
                this.localDate = new Date(d);
                // Langsung emit seperti BaseDatePicker (tidak perlu klik Apply)
                this.$emit("update:modelValue", new Date(d));
                this.closeModal();
            }
        },
        clearDate() {
            this.localDate = null;
            this.$emit("update:modelValue", null);
            this.closeModal();
        },
        applyDate() {
            if (this.localDate) {
                this.$emit("update:modelValue", new Date(this.localDate));
            }
            this.closeModal();
        },
        reposition() {
            if (!this.isModalOpen || this.dialog) return;
            const el = this.$el;
            if (!el) return;

            // Panel width mengikuti trigger
            if (this.matchTriggerWidth) {
                const w = Math.max(
                    this.minPanelWidth,
                    Math.min(el.offsetWidth, this.maxPanelWidth)
                );
                this.panelWidth = w;
            }

            const rect = el.getBoundingClientRect();
            const gap = 8,
                width = this.panelWidth,
                vw = window.innerWidth;

            let left =
                this.position === "right" ? rect.right - width : rect.left;
            left = Math.max(8, Math.min(left, vw - width - 8));
            const top = rect.bottom + gap;

            this.popoverStyle = { top: `${top}px`, left: `${left}px` };
        },
        handleClickOutside(event) {
            if (!this.isModalOpen) return;
            // Cek apakah klik di luar komponen dan popover
            const isOutsideComponent = !this.$el.contains(event.target);
            const isOutsidePopover = this.$refs.popover && !this.$refs.popover.contains(event.target);
            
            if (isOutsideComponent && isOutsidePopover) {
                this.closeModal();
            }
        },

        // ===== Helpers =====
        normalizeDate(v) {
            if (v === null || v === undefined || v === "") return null;
            const d = v instanceof Date ? v : new Date(v);
            return isNaN(d.getTime()) ? null : d;
        },
        today() {
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            return d;
        },
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);
        window.addEventListener("resize", this.reposition, { passive: true });
        window.addEventListener("scroll", this.reposition, { passive: true });

        // Inisialisasi awal bila kosong/invalid
        if (!this.normalizeDate(this.modelValue)) {
            this.localDate = null;
        } else {
            this.localDate = new Date(this.normalizedModel);
        }
    },
    beforeUnmount() {
        document.removeEventListener("click", this.handleClickOutside);
        window.removeEventListener("resize", this.reposition);
        window.removeEventListener("scroll", this.reposition);
    },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}
</style>
